/**
 * Format a duration in hours into a compact human-readable form.
 * Examples:
 *   formatHoursShort(0.5)    → "30m"
 *   formatHoursShort(3)      → "3h"
 *   formatHoursShort(26)     → "1d 2h"
 *   formatHoursShort(168)    → "7d"
 *   formatHoursShort(0)      → "now"
 */
export function formatHoursShort(hours) {
  if (hours <= 0) return "now";
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(hours * 60));
    return `${minutes}m`;
  }
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const remainder = Math.round(hours - days * 24);
  if (remainder === 24) return `${days + 1}d`;
  if (remainder === 0) return `${days}d`;
  return `${days}d ${remainder}h`;
}

/**
 * Format an absolute future ISO timestamp as a friendly short string.
 * Examples:
 *   formatNextRun(in 2 hours)  → "in 2h"
 *   formatNextRun(tomorrow)    → "in 1d 4h"
 *   formatNextRun(in 5 days)   → "Mon, May 11"
 */
export function formatNextRun(iso) {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const diffHours = (target.getTime() - Date.now()) / 3_600_000;
  if (diffHours <= 0) return "soon";
  if (diffHours < 48) return `in ${formatHoursShort(diffHours)}`;
  // Beyond 2 days — show a calendar label
  return target.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
/**
 * Compute the color + label metadata for a signal-heat score (0-100).
 */
export function heatBadgeMeta(score) {
  if (score >= 70) return { label: "HOT", color: "#FF3B30", bg: "bg-[#FF3B30]/20", text: "text-[#FF3B30]", border: "border-[#FF3B30]/30" };
  if (score >= 40) return { label: "WARM", color: "#F59E0B", bg: "bg-[#F59E0B]/20", text: "text-[#F59E0B]", border: "border-[#F59E0B]/30" };
  if (score >= 20) return { label: "TRACKED", color: "#00E0FF", bg: "bg-[#00E0FF]/15", text: "text-[#00E0FF]", border: "border-[#00E0FF]/30" };
  return null; // Don't render for cold/unknown signals
}
